const getLocalDayKey = (date) => {
  const target = date instanceof Date && !Number.isNaN(date.getTime()) ? date : new Date();
  const year = target.getFullYear();
  const month = String(target.getMonth() + 1).padStart(2, '0');
  const day = String(target.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getSeverityLabel = (severity) => (
  severity <= 3 ? 'Low' : severity <= 5 ? 'Moderate' : severity <= 8 ? 'High' : 'Critical'
);

export const buildBehaviorIncidentPayload = ({
  childId,
  incidentTime,
  incidentTypeOverride,
  customCategoryType,
  isCustomCategory,
  incidentName,
  severity,
  notes,
  selectedTriggers,
  triggerLabels,
  user,
  contextSnapshot,
  quickLogConfig = null,
}) => {
  const incidentTimestamp = new Date(incidentTime);
  const triggerText = triggerLabels.filter(Boolean).join(', ');
  const severityLabel = quickLogConfig?.severityPresets?.find((preset) => preset.value === severity)?.label || getSeverityLabel(severity);
  const incidentDayKey = getLocalDayKey(incidentTimestamp);
  const displayLabel = quickLogConfig?.timelineDisplayLabel || 'Response';
  const lensKey = quickLogConfig?.lensKey || null;
  const severityDisplayLabel = quickLogConfig?.severityLabel || 'Severity';
  const triggerNotesLabel = quickLogConfig?.triggerNotesLabel || 'Possible trigger';
  const triggerSummaryLabel = quickLogConfig?.triggerSummaryLabel || 'Possible context';

  const incidentData = {
    type: isCustomCategory ? customCategoryType : (incidentTypeOverride || 'other'),
    customIncidentName: isCustomCategory ? '' : incidentName.trim(),
    severity,
    remedy: triggerText || 'General observation and monitoring',
    triggerSummary: triggerText,
    suspectedTriggers: selectedTriggers,
    triggerNotes: notes.trim(),
    description: incidentName.trim(),
    incidentDateTime: incidentTimestamp.toISOString(),
    incidentDayKey,
    entryDateLabel: incidentTimestamp.toDateString(),
    authorId: user?.uid,
    authorName: user?.displayName || user?.email?.split('@')[0] || 'User',
    authorEmail: user?.email,
    adaptiveQuickLogLabel: displayLabel,
    adaptiveQuickLogLens: lensKey,
    adaptiveSeverityLabel: severityDisplayLabel,
    adaptiveTriggerNotesLabel: triggerNotesLabel,
    adaptiveTriggerSummaryLabel: triggerSummaryLabel,
  };

  const commonBehaviorDoc = {
    childId,
    collection: 'dailyLogs',
    status: 'active',
    createdBy: user?.uid || incidentData.authorId || null,
    timestamp: incidentTimestamp,
    entryDate: incidentData.entryDateLabel,
    entryDayKey: incidentData.incidentDayKey,
    category: 'log',
    type: 'log',
    timelineType: 'incident',
    entryType: 'incident',
    incidentStyle: true,
    incidentCategoryId: 'behavior',
    incidentCategoryLabel: displayLabel,
    incidentCategoryColor: '#E099B6',
    title: incidentData.description || displayLabel,
    titlePrefix: displayLabel,
    text: incidentData.description || displayLabel,
    content: [
      `${severityDisplayLabel}: ${severityLabel}`,
      notes.trim() ? `${triggerNotesLabel}: ${notes.trim()}` : null,
      incidentData.remedy ? `${triggerSummaryLabel}: ${incidentData.remedy}` : null,
    ].filter(Boolean).join(' • '),
    severity: incidentData.severity,
    severityLabel,
    triggerNotes: notes.trim(),
    triggerSummary: incidentData.triggerSummary,
    remedy: incidentData.remedy,
    suspectedTriggers: selectedTriggers,
    contextSnapshot,
    incidentData: {
      severity: incidentData.severity,
      severityLabel,
      triggerNotes: notes.trim(),
      triggerSummary: incidentData.triggerSummary,
      suspectedTriggers: selectedTriggers,
      remedy: incidentData.remedy,
      description: incidentData.description || '',
      incidentDateTime: incidentTimestamp.toISOString(),
      contextSnapshot,
      adaptiveSeverityLabel: severityDisplayLabel,
      adaptiveTriggerNotesLabel: triggerNotesLabel,
      adaptiveTriggerSummaryLabel: triggerSummaryLabel,
      followUpScheduled: false,
      followUpDate: null,
      followUpNotes: null,
      adaptiveQuickLogLabel: displayLabel,
      adaptiveQuickLogLens: lensKey,
    },
    authorId: incidentData.authorId,
    authorName: incidentData.authorName,
    authorEmail: incidentData.authorEmail,
    userId: user?.uid || incidentData.authorId || null,
  };

  return {
    incidentTimestamp,
    incidentData,
    commonBehaviorDoc,
  };
};
